import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, PowerOff } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { useAuth } from '../contexts/auth'
import { useActiveTenant } from '../contexts/tenant'
import type { Product, Tenant } from '@wsfull/shared'

const UNITS = ['UN', 'CX', 'KG', 'L', 'M', 'M2', 'M3', 'PC', 'PR', 'DZ', 'CT'] as const

type ProductForm = { tenantId: string; sku: string; name: string; barcode: string; unit: string; weightKg: string; unitsPerBox: string; thirdParty: boolean
  lengthCm: string; widthCm: string; heightCm: string }
const empty: ProductForm = { tenantId: '', sku: '', name: '', barcode: '', unit: 'UN', weightKg: '', unitsPerBox: '', thirdParty: false,
  lengthCm: '', widthCm: '', heightCm: '' }

export function ProductsPage() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin = !user?.tenantId
  const activeTenantId = useActiveTenant()
  const [modal, setModal] = useState<{ open: boolean; product?: Product }>({ open: false })
  const [form, setForm] = useState<ProductForm>(empty)

  const { data: clients = [] } = useQuery<Tenant[]>({
    queryKey: ['clients'],
    queryFn: async () => (await api.get('/clients')).data,
    enabled: isAdmin,
  })

  // Lista respeita o cliente selecionado na barra lateral (admin). Sem seleção → todos.
  const listTenantId = isAdmin ? activeTenantId : (user?.tenantId ?? '')
  const activeClient = clients.find(c => c.id === activeTenantId)

  const { data: products = [], isLoading } = useQuery<Product[]>({
    queryKey: ['products', listTenantId],
    queryFn: async () => (await api.get('/products', { params: listTenantId ? { tenantId: listTenantId } : {} })).data,
  })

  const openCreate = () => { setForm({ ...empty, tenantId: isAdmin ? activeTenantId : (user?.tenantId ?? '') }); setModal({ open: true }) }
  const openEdit = (p: Product) => {
    setForm({ tenantId: p.tenantId, sku: p.sku, name: p.name, barcode: (p as any).barcode ?? '', unit: p.unit, weightKg: p.weightKg?.toString() ?? '', unitsPerBox: (p as any).unitsPerBox?.toString() ?? '', thirdParty: (p as any).thirdParty ?? false,
      lengthCm: (p as any).lengthCm?.toString() ?? '', widthCm: (p as any).widthCm?.toString() ?? '', heightCm: (p as any).heightCm?.toString() ?? '' })
    setModal({ open: true, product: p })
  }
  const close = () => setModal({ open: false })

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        barcode: form.barcode.trim() || undefined,
        weightKg: form.weightKg ? parseFloat(form.weightKg) : undefined,
        // Medidas da CAIXA MASTER: é com elas que o packing list estima quantas
        // caixas cabem por camada no palete e, daí, a altura do volume.
        lengthCm: form.lengthCm ? parseFloat(form.lengthCm) : undefined,
        widthCm: form.widthCm ? parseFloat(form.widthCm) : undefined,
        heightCm: form.heightCm ? parseFloat(form.heightCm) : undefined,
        unitsPerBox: form.unitsPerBox ? parseInt(form.unitsPerBox) : undefined,
      }
      if (modal.product) {
        const { tenantId: _t, sku: _s, ...upd } = body
        await api.patch(`/products/${modal.product.id}`, upd)
      } else {
        await api.post('/products', body)
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['products'] }); close() },
  })

  const deactivate = useMutation({
    mutationFn: (id: string) => api.delete(`/products/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })

  const f = (k: keyof ProductForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }))

  const canSave = form.name && form.sku && (modal.product || form.tenantId)

  // Marcação em lote: os produtos entram às centenas pela sincronia do ERP, todos
  // com o padrão (fabricado pela WS). Sem o lote, a armazenagem de terceiros na
  // fatura ficava zerada e ninguém ia abrir a ficha de cada produto.
  const [marcando, setMarcando] = useState(false)
  const terceiros = products.filter(p => (p as any).thirdParty).length
  const podeEditar = user?.role !== 'client'

  async function marcarTodos(valor: boolean) {
    if (!listTenantId) { alert('Selecione um cliente no menu lateral — a marcação é por cliente.'); return }
    const quantos = valor ? products.length - terceiros : terceiros
    if (!quantos) { alert(valor ? 'Todos já estão marcados.' : 'Nenhum produto marcado.'); return }
    if (!window.confirm(
      `${valor ? 'Marcar' : 'Desmarcar'} ${quantos} produto(s) como de terceiro?` + '\n\n' +
      `Isso muda a armazenagem cobrada na fatura: produto de terceiro é cobrado, produto fabricado pela WS é isento.`
    )) return
    setMarcando(true)
    try {
      const { data: r } = await api.post('/products/terceiros', { tenantId: listTenantId, todos: true, valor })
      qc.invalidateQueries({ queryKey: ['products'] })
      alert(`${r.alterados} produto(s) atualizado(s). Gere o relatório do faturamento de novo para refletir.`)
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      alert(err.response?.data?.title ?? 'Falha ao marcar os produtos')
    } finally { setMarcando(false) }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Produtos</h2>
          <p className="text-sm text-gray-500">
            {products.length} produto(s)
            {isAdmin && (activeClient
              ? <> · <span className="font-medium text-blue-700">{activeClient.name}</span></>
              : <> · <span className="font-medium text-amber-600">todos os clientes</span> (selecione um no menu para separar)</>)}
          </p>
        </div>
        <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4" /> Novo produto</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : products.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum produto cadastrado</p>
          ) : (
            <div className="overflow-x-auto">
              {podeEditar && (
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-amber-50/50 px-5 py-2.5 text-xs text-amber-900">
                  <span>
                    <b>{terceiros}</b> de {products.length} marcado(s) como <b>de terceiro</b> —
                    é o que a fatura cobra de armazenagem (produto da WS é isento).
                  </span>
                  <div className="ml-auto flex gap-2">
                    <button className="rounded-lg border border-amber-300 bg-white px-2 py-1 font-medium hover:bg-amber-100 disabled:opacity-50"
                      disabled={marcando} onClick={() => marcarTodos(true)}>
                      Marcar todos como terceiro
                    </button>
                    <button className="rounded-lg border border-gray-300 bg-white px-2 py-1 font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                      disabled={marcando} onClick={() => marcarTodos(false)}>
                      Desmarcar todos
                    </button>
                  </div>
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Nome</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Unidade</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Un/Cx</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Peso/un (kg)</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Origem</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-mono font-medium text-gray-900">{p.sku}</td>
                      <td className="px-5 py-3 text-gray-700">{p.name}</td>
                      <td className="px-5 py-3"><Badge>{p.unit}</Badge></td>
                      <td className="px-5 py-3 text-right font-mono text-gray-700">{(p as any).unitsPerBox ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-600">{p.weightKg ?? '—'}</td>
                      {/* Origem decide a armazenagem na fatura: produto da WS é
                          isento, de terceiro é cobrado. Sem a coluna, não havia
                          como saber o que já estava marcado. */}
                      <td className="px-5 py-3">
                        {(p as any).thirdParty
                          ? <Badge variant="warning">Terceiro</Badge>
                          : <span className="text-xs text-gray-400">WS</span>}
                      </td>
                      <td className="px-5 py-3">
                        <Badge variant={p.active ? 'success' : 'danger'}>{p.active ? 'Ativo' : 'Inativo'}</Badge>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                          {p.active && (
                            <Button variant="ghost" size="sm" onClick={() => deactivate.mutate(p.id)}>
                              <PowerOff className="h-4 w-4 text-red-400" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title={modal.product ? 'Editar produto' : 'Novo produto'} open={modal.open} onClose={close}>
        <div className="space-y-4">
          {isAdmin && !modal.product && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Cliente *</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" value={form.tenantId} onChange={f('tenantId')}>
                <option value="">Selecione...</option>
                {clients.filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input label="SKU *" value={form.sku} onChange={f('sku')} placeholder="PROD-001" disabled={!!modal.product} />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Unidade</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" value={form.unit} onChange={f('unit')}>
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <Input label="Nome *" value={form.name} onChange={f('name')} placeholder="Nome do produto" />
          <Input label="Código de barras (EAN)" value={form.barcode} onChange={f('barcode')} placeholder="Opcional — para bipar com leitor/câmera" />
          <div className="grid grid-cols-2 gap-4">
            {/* Por UNIDADE: é assim que o packing list calcula o peso do palete
                (peso unitário × unidades). Rotular errado aqui multiplica o erro
                por milhares de unidades num documento de carga. */}
            <Input label="Peso por unidade (kg)" type="number" value={form.weightKg} onChange={f('weightKg')} placeholder="0.000" />
            <div className="sm:col-span-2">
              <p className="mb-1 text-sm font-medium text-gray-700">Caixa master (cm)</p>
              <div className="grid grid-cols-3 gap-2">
                <Input label="Comprimento" type="number" value={form.lengthCm} onChange={f('lengthCm')} placeholder="40" />
                <Input label="Largura" type="number" value={form.widthCm} onChange={f('widthCm')} placeholder="30" />
                <Input label="Altura" type="number" value={form.heightCm} onChange={f('heightCm')} placeholder="25" />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Medidas da caixa fechada (não da unidade). O packing list usa para estimar quantas caixas cabem
                por camada no palete 120×100 e a altura do volume.
              </p>
            </div>
            <Input label="Unidades por caixa" type="number" value={form.unitsPerBox} onChange={f('unitsPerBox')} placeholder="Ex: 12" />
          </div>
          <label className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            <input type="checkbox" className="mt-0.5" checked={form.thirdParty} onChange={e => setForm(p => ({ ...p, thirdParty: e.target.checked }))} />
            <span>
              <b>Produto de terceiro</b> (não fabricado pela WS)
              <span className="block text-xs text-gray-400">Cobra armazenagem e seguro no faturamento. Deixe desmarcado para produtos fabricados pela WS (isentos).</span>
            </span>
          </label>
          {save.error && <p className="text-sm text-red-600">{(save.error as any)?.response?.data?.title ?? 'Erro ao salvar'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={close}>Cancelar</Button>
            <Button loading={save.isPending} disabled={!canSave} onClick={() => save.mutate()}>
              {modal.product ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
